import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import Header from "../Header/Header";
import Loading from "../common/Loading";
import { fetchUsers, isAdminRole } from "../redux/action/users";
import UserFormDialog from "./UserFormDialog";
import { iconButtonSx, primaryButtonSx } from "../common/dialogStyles";
import { styles } from "../MainPage/MainPage";
import FilterHeaderCell, { ActiveFilters } from "../common/FilterHeaderCell";
import { filterOptions, matchesColumnFilters } from "../common/columnFilters";

const pageStyles = {
  content: {
    flex: 1,
    overflowY: "auto",
    padding: "2vh 2%",
    backgroundColor: "#F6F8FD",
    // App.css centres text app-wide (.App { text-align: center }), which would
    // otherwise centre the subtitle under the heading.
    textAlign: "left",
  },
  title: {
    color: "#212529",
    fontFamily: "manrope",
    fontSize: "22px",
    fontWeight: 600,
  },
  subtitle: {
    color: "#6C757D",
    fontFamily: "manrope",
    fontSize: "14px",
  },
  headCell: {
    fontFamily: "manrope",
    fontSize: "12px",
    fontWeight: 700,
    color: "#6C757D",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    whiteSpace: "nowrap",
  },
  cell: {
    fontFamily: "manrope",
    fontSize: "14px",
    color: "#212529",
  },
  storesCell: {
    fontFamily: "manrope",
    fontSize: "14px",
    color: "#212529",
    whiteSpace: "nowrap",
    width: "1%",
  },
  storeChips: { display: "flex", gap: "6px", alignItems: "center" },
  name: {
    fontFamily: "manrope",
    fontSize: "14px",
    fontWeight: 700,
    color: "#212529",
  },
  message: {
    fontFamily: "manrope",
    fontSize: "14px",
    color: "#6C757D",
    padding: "24px 0",
    textAlign: "center",
  },
};

const chipSx = {
  store: {
    fontFamily: "manrope",
    fontWeight: 700,
    fontSize: "12px",
    borderRadius: "8px",
    backgroundColor: "#EDF1FF",
    color: "#2050FF",
  },
  more: {
    fontFamily: "manrope",
    fontWeight: 700,
    fontSize: "12px",
    borderRadius: "8px",
    backgroundColor: "#EEF0F4",
    color: "#6C757D",
  },
  all: {
    fontFamily: "manrope",
    fontWeight: 600,
    fontSize: "12px",
    borderRadius: "8px",
    borderColor: "#E9ECEF",
    color: "#6C757D",
  },
  // Amber rather than grey: a non-admin with no stores can sign in and see
  // nothing, which is worth noticing in a list of rows.
  none: {
    fontFamily: "manrope",
    fontWeight: 600,
    fontSize: "12px",
    borderRadius: "8px",
    backgroundColor: "#FFF7E6",
    color: "#9A6700",
  },
};

// The header filters, one per column but Edit. Role and Status are picked from
// what the users hold; the rest are typed. Stores matches a store's code or
// name.
const COLUMN_FILTERS = [
  { field: "name", kind: "text" },
  { field: "email", kind: "text" },
  { field: "phone", kind: "text" },
  { field: "role", kind: "select" },
  { field: "stores", kind: "text" },
  { field: "status", kind: "select" },
];
const FILTER_LABELS = {
  name: "Name",
  email: "Email",
  phone: "Phone",
  role: "Role",
  stores: "Stores",
  status: "Status",
};

const fullName = (user) =>
  [user.firstName, user.lastName].filter(Boolean).join(" ") || "-";

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  // null = register, a user object = edit that user.
  const [editing, setEditing] = useState(null);

  // Registering a user needs the admin role; anyone else never sees the button.
  const login = useSelector((state) => state.login);
  const isAdmin = isAdminRole(login?.profile?.roleName);

  // A user's stores are store ids; the store list already in redux is what
  // turns them into names.
  const housesList = useSelector((state) => state.housesList);
  const storeById = useMemo(() => {
    const byId = {};
    for (let item of housesList) {
      byId[item._id] = item;
    }
    return byId;
  }, [housesList]);

  // field -> typed text or picked value, for the header filters.
  const [columnFilters, setColumnFilters] = useState({});
  const setColumnFilter = (field, value) =>
    setColumnFilters((current) => ({ ...current, [field]: value }));

  // Each user with the text its columns show, which is what the filters match.
  const rows = useMemo(
    () =>
      users.map((user) => ({
        user,
        name: fullName(user),
        email: user.emailAddress || "",
        phone: user.phoneNumber || "",
        role: user.roleName || "",
        status: user.status || "",
        stores: isAdminRole(user.roleName)
          ? "All stores"
          : (user.stores ?? [])
              .map((id) => `${storeById[id]?.code ?? id} ${storeById[id]?.houseName ?? ""}`)
              .join(" "),
      })),
    [users, storeById]
  );
  const shownUsers = useMemo(
    () =>
      rows
        .filter((row) => matchesColumnFilters(row, columnFilters, COLUMN_FILTERS))
        .map((row) => row.user),
    [rows, columnFilters]
  );
  // Shown capitalised, as the Role and Status cells are.
  const columnFilterOptions = useMemo(() => {
    const capitalised = (field) =>
      filterOptions(rows, field).map((option) => ({
        ...option,
        label: option.label.charAt(0).toUpperCase() + option.label.slice(1),
      }));
    return { role: capitalised("role"), status: capitalised("status") };
  }, [rows]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const result = await fetchUsers();

    if (result.success) {
      setUsers(result.data);
      setError("");
    } else {
      setUsers([]);
      setError(result.message);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    // Non-admins are redirected below; no point spending a request first.
    if (isAdmin) loadUsers();
  }, [loadUsers, isAdmin]);

  // Route guard. /users is admin only, and reaching it by typing the URL has to
  // be refused the same way the hidden nav tab refuses it. profile is undefined
  // while /v1/users/me is still in flight and null when that call failed, so
  // only a loaded profile naming the admin role is allowed through - anything
  // else fails closed.
  if (Object.keys(login).length === 0) {
    return <Navigate to="/" replace />;
  }

  if (login.profile === undefined) {
    return <Loading />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div style={{ ...styles.container }}>
      <div style={{ ...styles.rightContainer }}>
        <Header />

        <div style={pageStyles.content}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <div>
              <div style={pageStyles.title}>Registered Users</div>
              <div style={pageStyles.subtitle}>
                {loading
                  ? "Loading users..."
                  : `${users.length} user${users.length === 1 ? "" : "s"}${
                      shownUsers.length !== users.length ? ` · ${shownUsers.length} shown` : ""
                    }`}
              </div>
            </div>

            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<PersonAddAltIcon />}
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                  sx={primaryButtonSx}
                >
                  Register User
                </Button>
              )}

              <Tooltip title="Refresh users">
                <span>
                  <IconButton
                    onClick={loadUsers}
                    disabled={loading}
                    sx={iconButtonSx}
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <ActiveFilters
            specs={COLUMN_FILTERS}
            labels={FILTER_LABELS}
            filters={columnFilters}
            options={columnFilterOptions}
            onChange={setColumnFilter}
            onClear={() => setColumnFilters({})}
          />

          {/* Scrolls inside its own box so the header row stays pinned. */}
          <TableContainer
            component={Paper}
            sx={{ borderRadius: "10px", maxHeight: "calc(100vh - 200px)" }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMN_FILTERS.map(({ field, kind }) => (
                    <FilterHeaderCell
                      key={field}
                      label={FILTER_LABELS[field]}
                      kind={kind}
                      value={columnFilters[field]}
                      options={columnFilterOptions[field]}
                      onChange={(value) => setColumnFilter(field, value)}
                      Cell={TableCell}
                      cellProps={{ style: pageStyles.headCell }}
                    />
                  ))}
                  <TableCell style={pageStyles.headCell} align="right">
                    Edit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && error && (
                  <TableRow>
                    <TableCell colSpan={7} style={pageStyles.message}>
                      {error}
                    </TableCell>
                  </TableRow>
                )}

                {!loading && !error && users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} style={pageStyles.message}>
                      No registered users found.
                    </TableCell>
                  </TableRow>
                )}

                {!loading && !error && users.length > 0 && shownUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} style={pageStyles.message}>
                      No users match these filters.
                    </TableCell>
                  </TableRow>
                )}

                {shownUsers.map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell style={pageStyles.name}>
                      {fullName(user)}
                    </TableCell>
                    <TableCell style={pageStyles.cell}>
                      {user.emailAddress || "-"}
                    </TableCell>
                    <TableCell style={pageStyles.cell}>
                      {user.phoneNumber || "-"}
                    </TableCell>
                    <TableCell
                      style={{ ...pageStyles.cell, textTransform: "capitalize" }}
                    >
                      {user.roleName || "-"}
                    </TableCell>
                    <TableCell style={pageStyles.storesCell}>
                      {/* Three cases, and the role decides which. An admin
                          reaches every store whatever its list holds, so the
                          list is not shown at all - chips would read as a
                          limit. Anyone else is scoped to exactly what they
                          were given, so an empty list is no access, not all
                          access. Codes rather than names, capped at two with
                          the rest behind a +N: full names wrap to three lines
                          and leave every row a different height. */}
                      {isAdminRole(user.roleName) ? (
                        <Chip
                          label="All stores"
                          size="small"
                          variant="outlined"
                          sx={chipSx.all}
                        />
                      ) : user.stores?.length ? (
                        <div style={pageStyles.storeChips}>
                          {user.stores.slice(0, 2).map((id) => (
                            <Tooltip
                              key={id}
                              title={storeById[id]?.houseName || id}
                            >
                              <Chip
                                label={storeById[id]?.code || id}
                                size="small"
                                sx={chipSx.store}
                              />
                            </Tooltip>
                          ))}

                          {user.stores.length > 2 && (
                            <Tooltip
                              title={user.stores
                                .slice(2)
                                .map((id) => storeById[id]?.houseName || id)
                                .join(", ")}
                            >
                              <Chip
                                label={`+${user.stores.length - 2}`}
                                size="small"
                                sx={chipSx.more}
                              />
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <Chip label="No stores" size="small" sx={chipSx.none} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status || "unknown"}
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          fontFamily: "manrope",
                          fontWeight: 700,
                          backgroundColor:
                            user.status === "active" ? "#D1E7DD" : "#F8D7DA",
                          color:
                            user.status === "active" ? "#0F5132" : "#842029",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit user">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditing(user);
                            setFormOpen(true);
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {loading && <Loading />}

      <UserFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={loadUsers}
        user={editing}
        storeById={storeById}
      />
    </div>
  );
}

export default Users;
